import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, Printer, Table, History as HistoryIcon } from 'lucide-react';

interface SubHeaderExportDropdownProps {
  isGlobalView: boolean;
  viewMode: string;
  communicationTab: string;
  onOpenExportPro?: () => void;
  exportAsExcel: () => void;
  exportTaskTableAsExcel: () => void;
  handleDownloadRoadmap: () => void;
  onCloseOthers?: () => void;
}

export const SubHeaderExportDropdown: React.FC<SubHeaderExportDropdownProps> = ({
  isGlobalView,
  viewMode,
  communicationTab,
  onOpenExportPro,
  exportAsExcel,
  exportTaskTableAsExcel,
  handleDownloadRoadmap,
  onCloseOthers
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          onCloseOthers?.();
        }}
        className={`p-2 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-indigo-600 hover:bg-white dark:border-slate-800 rounded-xl transition-all h-10 border group relative flex items-center justify-center gap-1 px-2.5 ${
          isOpen ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : ''
        }`}
        title="Daten-Export"
      >
        <Download size={18} />
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50 space-y-1">
          {onOpenExportPro && (
            <button 
              type="button"
              onClick={() => { 
                setIsOpen(false); 
                onOpenExportPro(); 
              }} 
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left bg-gradient-to-r from-indigo-500/10 to-purple-500/10 hover:from-indigo-500/20 hover:to-purple-500/20 text-indigo-600 dark:text-indigo-400 rounded-xl transition-all group border border-indigo-500/20"
              title="Multiprojekt PDF & Drucker (DIN A3 / A4)"
            >
              <div className="w-6 h-6 rounded-md bg-indigo-500 text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm shrink-0">
                <Printer size={14} />
              </div>
              <span className="text-xs font-bold truncate">Export Pro (DIN A3/A4)</span>
            </button>
          )}

          {!isGlobalView && (
            <>
              <button 
                type="button"
                onClick={() => { 
                  exportAsExcel(); 
                  setIsOpen(false); 
                }} 
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
                title="Gesamte Projektmappe als Excel (XLSX) exportieren"
              >
                <div className="w-6 h-6 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform shrink-0">
                  <Table size={14} />
                </div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Projekt-Export</span>
              </button>

              <button 
                type="button"
                onClick={() => { 
                  exportTaskTableAsExcel(); 
                  setIsOpen(false); 
                }} 
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
                title="Aufgaben-Liste zeitlich sortiert als XLSX exportieren"
              >
                <div className="w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform shrink-0">
                  <Table size={14} />
                </div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Aufgaben-Liste</span>
              </button>
              
              <button 
                type="button"
                onClick={() => { 
                  handleDownloadRoadmap(); 
                  setIsOpen(false); 
                }} 
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-slate-800 rounded-xl transition-all group"
                title="Zeitstrahl als Bild speichern (PNG)"
              >
                <div className="w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform shrink-0">
                  <HistoryIcon size={14} />
                </div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Roadmap Download</span>
              </button>
            </>
          )}

          {(viewMode === 'analysis' || viewMode === 'impactEffort') && (
            <button 
              type="button"
              onClick={() => { 
                window.dispatchEvent(new CustomEvent('export-analysis')); 
                setIsOpen(false); 
              }} 
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition-all group ${!isGlobalView ? 'border-t border-slate-100 dark:border-slate-800/50 mt-1 pt-2' : ''}`}
              title="Aktuelle Analyseansicht als Bild exportieren"
            >
              <div className="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform shrink-0">
                <Download size={14} />
              </div>
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Analyse als Bild</span>
            </button>
          )}

          {viewMode === 'communication' && communicationTab === 'network' && (
            <button 
              type="button"
              onClick={() => { 
                window.dispatchEvent(new CustomEvent('export-contact-graph')); 
                setIsOpen(false); 
              }} 
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-xl transition-all group ${!isGlobalView ? 'border-t border-slate-100 dark:border-slate-800/50 mt-1 pt-2' : ''}`}
              title="Netzwerk als Bild exportieren"
            >
              <div className="w-6 h-6 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform shrink-0">
                <Download size={14} />
              </div>
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Netzwerk als Bild</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
